/**
 * GxTable: the one data table. Columns have a kind: `text` (left) or `number` (right, tabular figures); the unit sits once in the header (`unit`), the cells hold bare figures
 * (`figure()` from ./figures: whole numbers by default). Every row has the same height. The header stays in view when the table is long (`stickyAfter` rows).
 * Row kinds: `total` (bold, a rule above), `group` (a tinted band), `child` (indented, quieter), `fold` (a row that opens out of its parent: a dotted left edge and a marker, never the total's weight).
 */
import { useEffect, useRef, type ReactNode } from "react";
import { FIGURES, type FigureUnit } from "./figures";
import "./system.css";

export interface GxColumn {
  readonly key: string; readonly label: string; readonly kind?: "text" | "number"; readonly unit?: FigureUnit; readonly width?: string;
  /** The header's content when it is more than the label (a glossary term); `label` stays the plain name. */
  readonly header?: ReactNode;
  /** A secondary column: left out at laptop widths (the responsive set) so the table keeps room for the rest. */
  readonly wide?: boolean;
  /** Marked (the column a link points at). */
  readonly highlight?: boolean;
  /** Its part when the table stacks on a phone: `lead` (the figure on the row's first line, right), `under` (muted beneath the lead, with its label: the twin, last year), `line` (its own "label ... figure" line), `drop` (left out). Default: the first number column leads, the rest are lines. */
  readonly phone?: GxPhoneRole;
}
export type GxPhoneRole = "lead" | "under" | "line" | "drop";
/** How a table reads on a phone: `fit` (as on a laptop), `stack` (each row a block), `grid` (a true matrix: scrolls inside its own box, first column pinned). */
export type GxPhoneMode = "fit" | "stack" | "grid";

/** The phone mode: the explicit one, else `fit` for three columns or fewer, `stack` for more. */
export function phoneModeOf(columns: readonly GxColumn[], explicit?: GxPhoneMode): GxPhoneMode {
  return explicit ?? (columns.length <= 3 ? "fit" : "stack");
}
/** The stack role of each column (the first column is the row's label and has none): the column's own, else the first number column leads and the rest are lines. */
export function phoneRolesOf(columns: readonly GxColumn[]): readonly (GxPhoneRole | undefined)[] {
  const explicitLead = columns.some((c, i) => i > 0 && c.phone === "lead");
  let leadTaken = explicitLead;
  return columns.map((c, i) => {
    if (i === 0) return undefined;
    if (c.phone !== undefined) return c.phone;
    if (!leadTaken && (c.kind ?? "text") === "number") { leadTaken = true; return "lead"; }
    return "line";
  });
}
const labelOf = (c: GxColumn) => `${c.label}${c.unit === undefined || FIGURES[c.unit].header === "" ? "" : ` (${FIGURES[c.unit].header})`}`;

/** Six columns or more: too many to read as a row in a card of a tablet's width, so the stack mode holds up to the grid's breakpoint. */
const MANY_COLUMNS = 6;

const isEmpty = (v: ReactNode): boolean => v === undefined || v === null || v === false || v === "";

export type GxRowKind = "total" | "group" | "child" | "fold";
export interface GxRow { readonly key: string; readonly cells: Readonly<Record<string, ReactNode>>; readonly kind?: GxRowKind; /** The player's own row. */ readonly highlight?: boolean; /** The row a side panel is showing. */ readonly selected?: boolean; readonly testId?: string }

export function GxTable({ caption, labelledBy, columns, rows, stickyAfter = 12, dense = false, testId, phone }: {
  /** Read by a screen reader. */ readonly caption: string; /** The id of a visible heading that already names the table: it labels the table and no hidden caption is printed. */ readonly labelledBy?: string | undefined; readonly columns: readonly GxColumn[]; readonly rows: readonly GxRow[];
  readonly stickyAfter?: number; readonly dense?: boolean; readonly testId?: string;
  /** The phone mode (see GxPhoneMode); default by column count. */ readonly phone?: GxPhoneMode;
}) {
  const kindOf = (c: GxColumn) => (c.kind ?? "text");
  const mode = phoneModeOf(columns, phone);
  const roles = phoneRolesOf(columns);
  // A grid scrolls inside its own box, so the box is a named, focusable region (a keyboard can reach the overflow).
  const region = mode === "grid" ? { role: "region", tabIndex: 0, "aria-label": `${caption}, scrolls sideways` } : {};
  // The pinned first column's width, for the grid's snap padding (the next column starts clear of it).
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = box.current, first = el?.querySelector("thead th:first-child");
    if (mode !== "grid" || el === null || el === undefined || first === null || first === undefined || typeof ResizeObserver === "undefined") return;
    const set = () => el.style.setProperty("--gx-pin-w", `${Math.ceil(first.getBoundingClientRect().width)}px`);
    set();
    const observer = new ResizeObserver(set);
    observer.observe(first);
    return () => observer.disconnect();
  }, [mode, columns.length]);
  return (
    <div ref={box} className="gx-chart-table" data-phone={mode} {...region}>
      <table className={`gx-table gx-table--sys${dense ? " gx-table--dense" : ""}`} data-phone={mode} data-many={columns.length >= MANY_COLUMNS ? "" : undefined} data-sticky={rows.length > stickyAfter} data-testid={testId} aria-labelledby={labelledBy}>
        {labelledBy === undefined ? <caption className="gx-sr">{caption}</caption> : null}
        <thead>
          <tr>{columns.map((c) => (
            <th key={c.key} scope="col" className={kindOf(c) === "number" ? "is-num" : "is-text"} data-hl={c.highlight === true ? "" : undefined} data-wide={c.wide === true ? "" : undefined} {...(c.width === undefined ? {} : { style: { width: c.width } })}>
              {c.header ?? c.label}{c.unit === undefined || FIGURES[c.unit].header === "" ? null : <small className="gx-table-unit"> ({FIGURES[c.unit].header})</small>}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.kind === undefined ? undefined : `gx-row--${r.kind}`} data-kind={r.kind} data-hl={r.highlight === true ? "" : undefined} data-selected={r.selected === undefined ? undefined : r.selected} data-testid={r.testId}>
              {r.kind === "group" && columns.slice(1).every((c) => isEmpty(r.cells[c.key])) ? (
                // A band that only names its group spans the row: its words never sit inside the pinned first column of a grid (the label sticks inside the band instead).
                <th scope="row" colSpan={columns.length} className="is-text" data-label={labelOf(columns[0] as GxColumn)}><span className="gx-row-label">{r.cells[(columns[0] as GxColumn).key]}</span></th>
              ) : columns.map((c, i) => {
                const cls = kindOf(c) === "number" ? "is-num" : "is-text";
                const mark = { "data-hl": c.highlight === true ? "" : undefined, "data-wide": c.wide === true ? "" : undefined, "data-label": labelOf(c), "data-role": roles[i] };
                return i === 0 ? <th key={c.key} scope="row" className={cls} {...mark}>{r.cells[c.key]}</th> : <td key={c.key} className={cls} {...mark}>{r.cells[c.key]}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
